// A task's page, the parts said in words, pure (no React) so
// detailModel.selftest.ts runs it on its own (./TaskDetail.tsx,
// ./TaskParts.tsx).
//
// What came back: the real outputs first (as chips with their type and
// size), the run's own records (checkpoints.json, notes.md, result.json,
// result.md, items.json, anything under a dot) folded as "Working files". A live task,
// or a stop that only says so, with no answer gets no card (its working
// files sit under the brief); an ended one says it ended without an answer.
//
// Telling whoever asked: the usual case (every note Lattice owed them
// arrived, nothing to send again) is one quiet line under the header; only
// an exception (waiting, on its way, not delivered, maybe not arrived, sent
// somewhere else than asked, a resend offered) gets its own block.

import type { TaskDeliveryOut, TaskDetail, TaskSummary } from "@/api/tasks";
import type { StatusTone } from "@/components/ui/StatusBadge";

import type { StatusWord } from "../workflows/status";
import { minutesWords } from "../workflows/time";
import { cleanConclusion, isOwnTask, isStopNoteOnly, rowWho } from "./listModel";
import { NOTE_KIND, askerName, byWords, isLiveTask, planWaits, routingWords, taskStatusWord } from "./taskModel";

type NameOf = (template: string) => string;

export type TellView = { kind: "none" } | { kind: "card" } | { kind: "quiet"; text: string; at: string | null };

type Notes = { delivery: TaskDeliveryOut | null; notices: readonly TaskDeliveryOut[]; can: { resend: boolean } };

/** A note that went as asked: delivered, nothing to resend, no error, to the conversation it was given in. */
export function wentAsAsked(n: TaskDeliveryOut): boolean {
  return n.state === "delivered" && !n.can_resend && !n.last_error && !n.where;
}

function toldWords(kind: string, who: string): string {
  const you = who === "you";
  switch (kind) {
    case "stopped":
      return you ? "You were told it was stopped" : `${who} was told it was stopped`;
    case "refused":
      return you ? "You were told it was refused" : `${who} was told it was refused`;
    case "plan":
      return `Plan sent to ${who}`;
    case "decision":
      return `Decision sent to ${who}`;
    case "question":
      return `Question sent to ${who}`;
    case "answer":
      return `Answer sent to ${who}`;
    default:
      return `Sent to ${who}`;
  }
}

/**
 * How the page tells where the notes to whoever asked stand: nothing (none
 * owed), one quiet line ("Answer sent to Julia", with when), or the block.
 * `askerName` is the list's name for the asker ("You" for the operator);
 * `own`: an agent's task to itself ("Answer back in Soren's chat").
 */
export function tellView(task: Notes, askerName: string, { own = false }: { own?: boolean } = {}): TellView {
  const delivery = task.delivery && task.delivery.state !== "not_needed" ? task.delivery : null;
  const notes = [...(delivery ? [delivery] : []), ...task.notices];
  if (!notes.length) return { kind: "none" };
  if (task.can.resend || !notes.every(wentAsAsked)) return { kind: "card" };
  const who = askerName === "You" ? "you" : askerName;
  const main = notes[0];
  const more = notes.length - 1;
  // An agent's own task: the answer went back into its own chat.
  const told = own && main.kind === "answer" ? `Answer back in ${who}'s chat` : toldWords(main.kind, who);
  const text = told + (more ? `, with ${more} more ${more === 1 ? "note" : "notes"}` : "");
  // "a schedule was told …" opens the line: capitalised.
  return { kind: "quiet", text: text.charAt(0).toUpperCase() + text.slice(1), at: main.sent_at ?? main.at };
}

// ── waiting for your OK ──

/** "Julia asked Charlotte to do this, up to 3 h. Nothing starts until you approve." (`asker` as askerName says it: "You", "Julia", "a schedule"). */
export function approvalWords(asker: string, worker: string, minutes: number): string {
  const who = asker.charAt(0).toUpperCase() + asker.slice(1);
  const time = minutes > 0 ? `, up to ${minutesWords(minutes)}` : "";
  return `${who} asked ${worker} to do this${time}. Nothing starts until you approve.`;
}

// ── an answer that did not arrive ──

/** The answer to whoever asked did not arrive: the header says "Not delivered" instead of its status, beside Resend. */
export function isUndelivered(task: { delivery: TaskDeliveryOut | null }): boolean {
  return task.delivery?.state === "failed";
}

/** "Not delivered after 3 tries.", "Not delivered to a group chat." (the raw error goes behind Details). */
export function notDeliveredText(note: Pick<TaskDeliveryOut, "attempts" | "where">): string {
  const where = note.where ? ` to ${note.where}` : "";
  const tries = note.attempts > 1 ? ` after ${note.attempts} tries` : "";
  return `Not delivered${where}${tries}.`;
}

// ── what came back ──

const WORKING_FILES: ReadonlySet<string> = new Set(["checkpoints.json", "notes.md", "result.json", "result.md", "items.json"]);

/** A file of the run's own records, not something it made for you. */
export function isWorkingFile(name: string): boolean {
  const parts = name.split("/");
  return parts.some((p) => p.startsWith(".")) || WORKING_FILES.has(parts[parts.length - 1]);
}

/** The files a task handed back: its outputs, then its working files, each in the order given. */
export function splitFiles<T extends { name: string }>(files: readonly T[]): { outputs: T[]; working: T[] } {
  return { outputs: files.filter((f) => !isWorkingFile(f.name)), working: files.filter((f) => isWorkingFile(f.name)) };
}

const FILE_TYPE: Readonly<Record<string, string>> = {
  pdf: "PDF", doc: "Word", docx: "Word", xls: "Excel", xlsx: "Excel", csv: "CSV", ppt: "PowerPoint", pptx: "PowerPoint",
  md: "Markdown", txt: "Text", json: "JSON", html: "Web page", ipynb: "Notebook", py: "Python", zip: "ZIP", parquet: "Parquet",
  png: "Image", jpg: "Image", jpeg: "Image", gif: "Image", webp: "Image", svg: "Image",
  mp3: "Audio", m4a: "Audio", wav: "Audio", mp4: "Video", mov: "Video",
};

/** A file's kind in a word ("PDF", "Word", "Image"); its extension in capitals otherwise; "" without one. */
export function fileTypeWord(name: string): string {
  const base = name.split("/").pop() ?? name;
  const dot = base.lastIndexOf(".");
  if (dot <= 0 || dot === base.length - 1) return "";
  const ext = base.slice(dot + 1).toLowerCase();
  return FILE_TYPE[ext] ?? ext.toUpperCase();
}

type Answered = { status: string; conclusion: string | null; result_md: string | null; files: readonly { name: string }[] };

/**
 * Whether the page shows "What came back": "answer" (a conclusion, a
 * result or a real file), "empty" (it ended with none: it says so), or
 * "none" (still live, or a stop that only says so: no card).
 */
export function answerShown(task: Answered): "answer" | "empty" | "none" {
  const said = !!task.conclusion?.trim() && !isStopNoteOnly(task);
  if (said || task.result_md || splitFiles(task.files).outputs.length) return "answer";
  if (isLiveTask(task) || isStopNoteOnly(task)) return "none";
  return "empty";
}

// ── result.md, only what it adds ──

const words = (text: string): string[] => text.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
const same = (a: string, b: string): boolean => words(a).join(" ") === words(b).join(" ");

/**
 * result.md as the answer card shows it under the bold conclusion: without
 * a first heading that only repeats the task's title, and not at all when
 * all that is left is one short paragraph saying what the conclusion says
 * (at most 2 lines, and over 70% of its words in the conclusion). ""
 * when there is nothing more to show.
 */
export function resultShown(resultMd: string | null | undefined, conclusion: string, titles: readonly string[]): string {
  let md = (resultMd ?? "").trim();
  const h1 = /^#\s+(.+?)\s*#*\s*(?:\n|$)/.exec(md);
  if (h1 && titles.some((t) => t.trim() && same(h1[1], t))) md = md.slice(h1[0].length).trim();
  if (!md || !conclusion.trim()) return md;
  const lines = md.split("\n");
  const plainParagraph = lines.length <= 2 && !/^\s*(?:[#>|*+-]|\d+[.)])/m.test(md);
  if (!plainParagraph) return md;
  const said = new Set(words(conclusion));
  const own = [...new Set(words(md))];
  const repeated = own.filter((w) => said.has(w)).length / Math.max(1, own.length);
  return repeated > 0.7 ? "" : md;
}

// ── the header ──

/**
 * Who did it and for whom, as the header says it: "Quant" and "for Julia",
 * "for you", "own task" (an agent's task to itself), "on a schedule", "from a
 * to-do", "from another task". A workflow named like the task is "Workflow".
 */
export function forWhomWords(t: Pick<TaskDetail, "asker" | "worker" | "title">, nameOf: NameOf): { who: string; forWhom: string } {
  const { name } = rowWho(t, nameOf);
  const a = t.asker;
  if (a.kind === "operator") return { who: name, forWhom: "for you" };
  if (a.kind === "schedule") return { who: name, forWhom: "on a schedule" };
  if (a.kind === "todo") return { who: name, forWhom: "from a to-do" };
  if (a.kind === "task") return { who: name, forWhom: "from another task" };
  if (isOwnTask(t)) return { who: name, forWhom: "own task" };
  return { who: name, forWhom: `for ${askerName(a, nameOf)}` };
}

/** The status the header shows: "Not delivered" while the answer did not arrive, else the task's own word. */
export function headerStatus(t: Pick<TaskDetail, "status" | "delivery">): StatusWord {
  if (isUndelivered(t)) return { ...taskStatusWord("failed"), label: "Not delivered" };
  return taskStatusWord(t.status);
}

/**
 * The one page of a piece of work. A task is one person doing one thing: its
 * own page. A workflow's or a Team task's page is the run's or the Team
 * task's, so the task's row goes there, unless the task itself still needs
 * the operator (its OK, an answer to send again: only its own page has those).
 */
export function workPage(t: Pick<TaskSummary, "worker" | "status" | "answer_waiting" | "run_path" | "approval">): string | null {
  const elsewhere = t.worker.kind === "workflow" || t.worker.kind === "team";
  const ownOk = t.status === "awaiting_approval" && !planWaits(t);
  return elsewhere && !ownOk && !t.answer_waiting && t.run_path ? t.run_path : null;
}

/** When the header dates it: the end once it ended ("finished"), the start while it runs, the give before. */
export function headerWhen(t: Pick<TaskDetail, "status" | "created_at" | "started_at" | "ended_at">): { verb: string; at: string } {
  if (!isLiveTask(t) && t.ended_at) return { verb: t.status === "stopped" ? "stopped" : t.status === "finished" || t.status === "partial" ? "finished" : "ended", at: t.ended_at };
  if (t.started_at && t.status !== "awaiting_approval" && t.status !== "queued") return { verb: "started", at: t.started_at };
  return { verb: "given", at: t.created_at };
}

type Rounds = Pick<TaskDetail, "started_at" | "round" | "rounds" | "follow_ups">;

/**
 * When the task's current round started. The first round: its start. A
 * reopened task: `rounds` holds only the rounds that ended before a reply
 * reopened it (services/tasks/dispatch._open_round), so the current one
 * started when the first reply sent after the last of them went out (an
 * older record of the current round is used when there is one). Null when
 * it cannot be told.
 */
export function roundStartOf(t: Rounds): string | null {
  if (t.round <= 0) return t.started_at;
  const own = t.rounds.find((r) => r.round === t.round)?.started_at;
  if (own) return own;
  const ends = t.rounds.filter((r) => r.round < t.round).map((r) => Date.parse(r.ended_at ?? "")).filter(Number.isFinite);
  const after = ends.length ? Math.max(...ends) : Number.NEGATIVE_INFINITY;
  const opened = t.follow_ups
    .filter((f) => f.state === "sent" && Number.isFinite(Date.parse(f.sent_at ?? "")) && Date.parse(f.sent_at ?? "") >= after)
    .sort((a, b) => Date.parse(a.sent_at ?? "") - Date.parse(b.sent_at ?? ""))[0];
  return opened?.sent_at ?? null;
}

/** The live panel's time bar: how far through its time limit it is (0..1), null without a start or a limit. */
export function timeUsed(t: Rounds & Pick<TaskDetail, "minutes" | "status">, now: number): number | null {
  // Queued, its time has not started again: a reopened one still carries its last round's start.
  if (t.status === "queued") return null;
  // A reopened task runs from its current round's start; without one, no bar (its words fall back to the time used).
  const since = roundStartOf(t);
  const start = Date.parse(since ?? "");
  if (!Number.isFinite(start) || !(t.minutes > 0)) return null;
  return Math.min(1, Math.max(0, (now - start) / (t.minutes * 60_000)));
}

/**
 * Why a task ran out of time, as the panel says it: its own words when they
 * already say so ("Out of time after 60 min with 14 prices found."), else
 * "It ran out of time after 1 h." and whatever it said.
 */
export function outOfTimeWords(minutes: number, conclusion: string | null | undefined): { lead: string; rest: string } {
  const said = cleanConclusion(conclusion);
  if (/^out of time\b/i.test(said)) return { lead: "", rest: said };
  return { lead: `It ran out of time${minutes > 0 ? ` after ${minutesWords(minutes)}` : ""}.`, rest: said };
}

// ── the conversation ──

export interface EarlierAnswer {
  at: string;
  /** What it came back with ("It ended without an answer." when nothing). */
  text: string;
  /** How that round ended, when not simply answered. */
  status: { label: string; tone: StatusTone } | null;
}

/**
 * A reopened task's earlier answers, oldest first, for the thread under
 * the answer: what each round that ended before a reply reopened it came
 * back with, so the replies read as a conversation and not as your words
 * alone (the page's own answer is the latest round's).
 */
export function earlierAnswers(t: Pick<TaskDetail, "round" | "rounds">): EarlierAnswer[] {
  return t.rounds
    .filter((r) => r.round < t.round && !!r.ended_at)
    .sort((a, b) => a.round - b.round)
    .map((r) => {
      const ended = ROUND_ENDED[r.status];
      const status = ended && ended.text !== "Answered" ? { label: ended.text, tone: ended.tone ?? "neutral" } : null;
      return { at: r.ended_at as string, text: cleanConclusion(r.conclusion) || "It ended without an answer.", status };
    });
}

/**
 * How the thread of a reopened task ends once its latest round ended: "answered"
 * (the answer is the top of the page), "failed", "ran out of time", "was stopped";
 * null for a task never reopened or still running.
 */
export function latestRoundWords(t: Pick<TaskDetail, "round" | "status" | "ended_at">): { verb: string; at: string; above: boolean } | null {
  if (t.round <= 0 || isLiveTask(t) || !t.ended_at) return null;
  const verb = LATEST_ENDED[t.status] ?? "ended";
  return { verb, at: t.ended_at, above: t.status === "finished" || t.status === "partial" };
}

const LATEST_ENDED: Readonly<Record<string, string>> = { finished: "answered", partial: "answered, partly", failed: "failed", timed_out: "ran out of time", stopped: "was stopped" };

// ── how it ran: the timeline ──

export interface TimelineItem {
  at: string;
  /** What happened, in words. */
  text: string;
  /** A quoted line under it (a reply, a question, a conclusion). */
  quote?: string;
  tone?: StatusTone;
}

/** How an earlier round ended (its node's status), in the timeline. */
const ROUND_ENDED: Readonly<Record<string, { text: string; tone?: StatusTone }>> = {
  completed: { text: "Answered" },
  completed_with_failures: { text: "Answered, partly" },
  failed: { text: "Failed", tone: "danger" },
  timed_out: { text: "Ran out of time", tone: "warning" },
  cancelled: { text: "Stopped" },
};

const ENDED_WORDS: Readonly<Record<string, string>> = { finished: "Finished", partial: "Finished, partly done", failed: "Failed", timed_out: "Ran out of time", stopped: "Stopped" };

/** Said about an agent mid-sentence: "you" for the operator. */
const youOr = (name: string) => (name === "You" ? "you" : name);

/**
 * How it ran, in words and in order: who gave it to whom (and the limit),
 * why that worker (Corvus chose), the start, every reply and question with
 * their answers, each round's end when it was reopened, how it ended, and
 * where the answer and the other notes went. Built from the task itself,
 * not from the engine's log (that is the raw log behind it).
 */
export function timelineOf(t: TaskDetail, nameOf: NameOf): TimelineItem[] {
  const out: TimelineItem[] = [];
  const { who } = forWhomWords(t, nameOf);
  const asker = askerName(t.asker, nameOf);
  // A reopened task's `minutes` is its current round's limit (the reply's), not the one it was given with.
  const limit = t.minutes > 0 && t.round === 0 ? ` (${minutesWords(t.minutes)} limit)` : "";
  const given =
    t.asker.kind === "operator"
      ? `You gave it to ${who}${limit}`
      : t.asker.kind === "schedule"
        ? `Its schedule gave it to ${who}${limit}`
        : isOwnTask(t)
          ? `${who} gave it to itself${limit}`
          : `${asker.charAt(0).toUpperCase()}${asker.slice(1)} gave it to ${who}${limit}`;
  out.push({ at: t.created_at, text: given });
  const routing = routingWords(t.routing, who);
  if (routing.picked) out.push({ at: t.created_at, text: routing.picked.replace(/\.$/, "") });
  if (routing.offer) out.push({ at: t.created_at, text: routing.offer });
  if (t.status === "awaiting_approval") out.push({ at: t.created_at, text: "Waiting for you", tone: "warning" });
  if (t.started_at && t.status !== "awaiting_approval") out.push({ at: t.started_at, text: "Started" });
  for (const f of t.follow_ups) {
    const by = byWords(f.by, nameOf);
    const state = f.state === "queued" ? " (waiting for its turn to end)" : f.state === "refused" ? " (refused)" : f.state === "cancelled" ? " (stopped)" : "";
    out.push({ at: f.created_at, text: `${by === "you" ? "You" : by} replied${state}`, quote: f.text, tone: f.state === "refused" ? "danger" : undefined });
  }
  for (const q of t.questions) {
    out.push({ at: q.asked_at, text: `It asked ${youOr(nameOf(q.to_agent))}`, quote: q.text, tone: q.state === "open" ? "info" : undefined });
    if (q.answered_at && q.answer) out.push({ at: q.answered_at, text: `${nameOf(q.to_agent)} answered`, quote: q.answer });
    else if (q.state === "expired") out.push({ at: q.asked_at, text: "No answer came in time", tone: "warning" });
  }
  // A reopened task: what each earlier round came back with, and when the reply took it up again.
  for (const r of t.rounds) {
    if (r.round > 0 && r.started_at) out.push({ at: r.started_at, text: "Started again" });
    if (r.ended_at && r.round < t.round) {
      const ended = ROUND_ENDED[r.status] ?? { text: "Answered" };
      out.push({ at: r.ended_at, text: ended.text, quote: cleanConclusion(r.conclusion) || undefined, tone: ended.tone });
    }
  }
  if (t.round > 0 && !t.rounds.some((r) => r.round === t.round)) {
    const again = roundStartOf(t);
    if (again) out.push({ at: again, text: "Started again" });
  }
  if (!isLiveTask(t) && t.ended_at) {
    const word = ENDED_WORDS[t.status] ?? taskStatusWord(t.status).label;
    if (t.status === "stopped" && t.stop) {
      out.push({ at: t.stop.at || t.ended_at, text: `Stopped by ${byWords(t.stop.by, nameOf)}`, quote: t.stop.reason || undefined });
    } else {
      const reason = t.status === "finished" ? "" : cleanConclusion(t.conclusion);
      out.push({ at: t.ended_at, text: word, quote: reason || undefined, tone: t.status === "failed" ? "danger" : t.status === "finished" ? "success" : "warning" });
    }
  }
  const notes = [...(t.delivery && t.delivery.state !== "not_needed" ? [t.delivery] : []), ...t.notices];
  for (const n of notes) {
    const what = NOTE_KIND[n.kind] ?? "A note";
    const to = n.where ? ` (${n.where})` : "";
    if (n.state === "delivered") out.push({ at: n.sent_at ?? n.at ?? t.ended_at ?? t.created_at, text: `${what} sent to ${youOr(asker)}${to}` });
    else if (n.state === "failed") out.push({ at: n.at ?? t.ended_at ?? t.created_at, text: `${notDeliveredText(n).replace(/\.$/, "")}: ${what.toLowerCase()} to ${youOr(asker)}`, tone: "danger" });
    else if (n.state === "uncertain") out.push({ at: n.at ?? t.created_at, text: `${what} to ${youOr(asker)} may not have arrived`, tone: "warning" });
    else if (n.state === "pending" || n.state === "sending") out.push({ at: n.at ?? t.created_at, text: `${what} to ${youOr(asker)}: on its way`, tone: "info" });
  }
  // Stable by time (the order above breaks ties: given before started, a question before its answer).
  return out.map((item, i) => ({ item, i })).sort((a, b) => Date.parse(a.item.at) - Date.parse(b.item.at) || a.i - b.i).map(({ item }) => item);
}
